import type React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
  Tooltip,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { motion } from "framer-motion";
import type { DesignResponse } from "../types/figma";
import { PlayIcon } from "../../../components/icons";
import apiService from "../../../service/Api";
import { useState } from "react";
import { Delete, Favorite } from "@mui/icons-material";

interface DesignCardProps {
  design: DesignResponse;
  index: number;
  fetchDesigns: () => Promise<void>;
}

export const DesignCard: React.FC<DesignCardProps> = ({
  design,
  index,
  fetchDesigns,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [hovered, setHovered] = useState(false);

  // Open Figma link
  const handleOpenFigma = () => {
    if (!design.figma_link) return;
    window.open(design.figma_link, "_blank", "noopener,noreferrer");
  };

  // Mark as favourite
  const handleFavourite = async () => {
    try {
      await apiService.patch(`/api/figma-designs/figma-design/${design.id}/`, {
        is_favourite: true,
      });
      await fetchDesigns();
    } catch (err) {
      console.error("Failed to favourite design:", err);
    }
  };

  // Delete design
  const handleDelete = async () => {
    try {
      await apiService.delete(`/api/figma-designs/figma-design/${design.id}/`);
      await fetchDesigns();
    } catch (err) {
      console.error("Failed to delete design:", err);
    }
  };

  const categoryName = design.category?.name || "Unknown";
  const categoryColor = design.category?.category_color || "#999";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      whileHover={{ y: -6 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Card
        sx={{
          borderRadius: 4,
          border: `1px solid ${colors.primary[300]}`,
          boxShadow: 0,
          transition: "all 0.25s ease",
          backgroundColor: colors.primary[900],
          "&:hover": {
            boxShadow: "0px 8px 20px rgba(0,0,0,0.08)",
          },
          position: "relative",
        }}
      >
        <Box sx={{ position: "relative", p: 2 }}>
          <Box
            sx={{
              borderRadius: 3,
              border: `1px solid ${colors.primary[300]}`,
              overflow: "hidden",
              backgroundColor: "#F9FAFB",
              position: "relative",
            }}
          >
            <Box
              component="img"
              src={design.design_image}
              alt={design.name}
              sx={{
                width: "100%",
                height: 220,
                objectFit: "cover",
                display: "block",
              }}
              loading="lazy"
            />

            {/* Category Chip — TOP LEFT */}
            <Chip
              label={categoryName}
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                height: 26,
                px: 1.5,
                borderRadius: 999,
                backgroundColor: categoryColor,
                color: "white",
                fontWeight: 600,
                fontSize: 11,
                zIndex: 2,
              }}
            />

            {/* Hover actions — TOP RIGHT */}
            {hovered && (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  display: "flex",
                  gap: 1,
                  zIndex: 3,
                }}
              >
                <Tooltip title="Favourite">
                  <IconButton
                    size="small"
                    onClick={handleFavourite}
                    sx={{
                      bgcolor: "#FFD700",
                      "&:hover": { bgcolor: "#FFC107" },
                    }}
                  >
                    <Favorite fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={handleDelete}
                    sx={{
                      bgcolor: "#F44336",
                      "&:hover": { bgcolor: "#D32F2F" },
                      color: "#fff",
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>

        <CardContent
          sx={{
            px: 2.5,
            pb: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.4 }}>
              {design.name}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
              {design.design_type_display || design.description}
            </Typography>
          </Box>

          <IconButton
            onClick={handleOpenFigma}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: "#2196F3",
              color: "white",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#1976D2",
              },
            }}
          >
            <PlayIcon />
          </IconButton>
        </CardContent>
      </Card>
    </motion.div>
  );
};
